import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Loader2, ChevronRight } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { API, TRANSLATIONS, authHeader } from "./shared";
import { SkeletonSearch, EmptyState, EXAMPLE_SEARCHES } from "./_shared_ui";

export default function BibleSearchPanel({ t }) {
  const [query, setQuery] = useState("");
  const [translation, setTranslation] = useState("kjv");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lookups, setLookups] = useState({});

  const doSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/api/pulpit/bible/search`,
        { query: query.trim(), translation }, { headers: authHeader() });
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, t("search_failed") || "Search failed"));
    } finally { setLoading(false); }
  };

  const expandVerse = async (reference) => {
    if (lookups[reference]) {
      setLookups((s) => ({ ...s, [reference]: { ...s[reference], expanded: !s[reference].expanded } }));
      return;
    }
    try {
      const res = await axios.get(`${API}/api/pulpit/bible/lookup`, {
        params: { reference, translation }, headers: authHeader(),
      });
      setLookups((s) => ({ ...s, [reference]: { data: res.data, expanded: true } }));
    } catch (err) {
      toast.error(getErrorMessage(err, t("lookup_failed") || "Lookup failed"));
    }
  };

  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardHeader>
        <CardTitle className="text-base">{t("bible_search_cross_ref") || "Bible Search & Cross-References"}</CardTitle>
        <CardDescription>{t("search_desc") || "Find verses by topic with AI-powered cross-references"}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          <Input data-testid="search-query-input" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder={t("search_placeholder") || "topic, keyword, or theme..."} className="flex-1" />
          <Select value={translation} onValueChange={setTranslation}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRANSLATIONS.map((tx) => <SelectItem key={tx.value} value={tx.value}>{tx.value.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={doSearch} disabled={loading} data-testid="run-search-btn" className="bg-amber-600 hover:bg-amber-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        <ScrollArea className="h-[500px]">
          {loading ? (
            <SkeletonSearch />
          ) : !result ? (
            <EmptyState
              icon={Search}
              title={t("search_empty_title") || "Find verses on any topic"}
              description={t("search_empty_desc") || "Type a theme, keyword, or question — AI finds verses with cross-references."}
              examples={EXAMPLE_SEARCHES}
              onPick={(ex) => { setQuery(ex.query); }}
              testId="search-empty"
            />
          ) : (
            <div className="space-y-4" data-testid="search-results">
              <section>
                <h3 className="text-xs uppercase text-amber-400 mb-2">{t("primary_verses") || "Primary Verses"}</h3>
                <div className="space-y-2">
                  {(result.verses || []).map((v, i) => (
                    <div key={i} className="p-3 rounded border border-slate-800 bg-slate-950/40">
                      <button onClick={() => expandVerse(v.reference)} className="text-amber-300 font-semibold text-sm flex items-center gap-1 hover:underline" data-testid={`expand-verse-${i}`}>
                        <ChevronRight className={`w-3 h-3 transition-transform ${lookups[v.reference]?.expanded ? "rotate-90" : ""}`} />
                        {v.reference}
                      </button>
                      <p className="text-xs text-slate-300 mt-1">{v.summary}</p>
                      {lookups[v.reference]?.expanded && lookups[v.reference]?.data && (
                        <div className="mt-2 p-2 rounded bg-slate-900/60 text-sm text-slate-200 border-l-2 border-amber-500/40">
                          {lookups[v.reference].data.text}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
              {result.cross_references?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase text-amber-400 mb-2">{t("cross_references") || "Cross References"}</h3>
                  <div className="space-y-2">
                    {result.cross_references.map((cr, i) => (
                      <div key={i} className="p-2 rounded border border-slate-800 bg-slate-950/40">
                        <button onClick={() => expandVerse(cr.reference)} className="text-amber-300 text-sm hover:underline flex items-center gap-1">
                          <ChevronRight className={`w-3 h-3 transition-transform ${lookups[cr.reference]?.expanded ? "rotate-90" : ""}`} />
                          {cr.reference}
                        </button>
                        <p className="text-xs text-slate-400 mt-0.5">{cr.relation}</p>
                        {lookups[cr.reference]?.expanded && lookups[cr.reference]?.data && (
                          <div className="mt-1 p-2 rounded bg-slate-900/60 text-xs text-slate-200 border-l-2 border-amber-500/40">
                            {lookups[cr.reference].data.text}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
